import type { WebsocketBidEvent } from '@freelancer/datastore/core';
import { assertNever, isDefined } from '@freelancer/utils';
import {
  type BidApi,
  type BidShowcaseItemApi,
  DisplayBidRelevancyLabelApi,
  type BidAnnotationApi,
  BidAnnotationContextTypeApi,
} from 'api-typings/projects/projects';
import type { BidVectorSearchAnnotationResultAjax } from '../bid-vector-search-annotations/bid-vector-search-annotations.backend-model';
import type { BidVectorSearchContextType } from '../bid-vector-search-relevancy-summary/bid-vector-search-relevancy-summary.backend-model';
import {
  transformBid,
  transformWebsocketBidEvent,
} from '../bids/bids.transformers';
import { OnlineOfflineUserStatusValueApi } from '../online-offline';
import {
  BID_RATING_BACKEND_SCALING_FACTOR,
  type BidAnnotation,
  type BidShowcaseItem,
  type ProjectViewBid,
} from './project-view-bids.model';
import { transformThumbnail } from '../showcase/showcase.transformers';

export function transformProjectViewBids(
  projectViewBid: BidApi,
  recommendedBids?: readonly BidApi[],
): ProjectViewBid {
  const bid = transformBid(projectViewBid);
  const isRecommended = !!recommendedBids?.some(
    recommendedBid => recommendedBid.id === projectViewBid.id,
  );

  // Scale down the rating value from backend [0, 100] to frontend [0, 5]
  const ratingValue = isDefined(projectViewBid.rating?.rating)
    ? projectViewBid.rating.rating / BID_RATING_BACKEND_SCALING_FACTOR
    : undefined;

  const relevanceLabel =
    projectViewBid.display_relevance_label ?? DisplayBidRelevancyLabelApi.A;

  const annotation =
    projectViewBid.annotation !== undefined
      ? transformBidAnnotation(projectViewBid.annotation)
      : undefined;

  return {
    ...bid,
    score:
      projectViewBid.score !== undefined
        ? projectViewBid.score
        : Number.NEGATIVE_INFINITY,
    recommended: isRecommended,
    isVideoBid: !!projectViewBid.pitch_id,
    showcases: projectViewBid.showcases?.map(transformBidShowcaseItem) ?? [],
    ratingValue,
    bidderCountryCode: projectViewBid.bidder_country?.code?.toLowerCase(),
    isBidderFreelancerVerified: projectViewBid.is_bidder_freelancer_verified,
    isBidderPreferredFreelancer: projectViewBid.is_bidder_preferred_freelancer,
    bidderReviewCount: projectViewBid.bidder_review_count,
    // Scale up from [0,1] to [0, 100]
    bidderJobCompletionRate: isDefined(
      projectViewBid.bidder_job_completion_rate,
    )
      ? projectViewBid.bidder_job_completion_rate * 100 // scale from decimal to percent
      : undefined,
    bidderOverallRating: projectViewBid.bidder_overall_rating,
    isBidderOnline: projectViewBid.is_bidder_online,
    adjustedScore: projectViewBid.adjusted_score,
    relevanceLabel,
    newBidRank: projectViewBid.new_bid_rank,
    originalBidRank: projectViewBid.original_bid_rank,
    annotation,
    language: projectViewBid.language,
  };
}

export function transformBidShowcaseItem(
  bidShowcaseItem: BidShowcaseItemApi,
): BidShowcaseItem {
  return {
    id: bidShowcaseItem.id,
    bidId: bidShowcaseItem.bid_id,
    showcaseId: bidShowcaseItem.showcase_id,
    position: bidShowcaseItem.position,
    /**
     * filename can be undefined when:
     *  - showcase has `NULL` `cover_item_id`
     *  - showcase has no items
     *
     * Added an empty string fallback instead of throwing an error to not cascade the error to
     * `transformProjectViewBids` which will cause the bid list to not load.
     */
    filename: bidShowcaseItem.filename ?? '',
    thumbnail: bidShowcaseItem.thumbnail
      ? transformThumbnail(bidShowcaseItem.thumbnail)
      : undefined,
  };
}

// score, recommended, and isVideoBid is omitted from the return type
// because they are handled in project-view-bids.reducer.ts
export function transformWebsocketProjectViewBidEvent(
  event: WebsocketBidEvent,
  extra: {
    readonly recommended: boolean;
    readonly score: number;
    readonly relevanceLabel: DisplayBidRelevancyLabelApi;
  },
): Omit<
  ProjectViewBid,
  'score' | 'recommended' | 'isVideoBid' | 'relevanceLabel'
> {
  return {
    ...transformWebsocketBidEvent(event),
    showcases: event.data.bid.showcases ?? [],
    adjustedScore: event.data.bid.adjusted_score,
    // annotation is not available in WebsocketBidEvent,
    annotation: undefined,
    language: event.data.bid.language,
  };
}

export function transformIsBidderOnlineOffline(
  status: OnlineOfflineUserStatusValueApi,
): boolean {
  switch (status) {
    case OnlineOfflineUserStatusValueApi.ONLINE:
      return true;
    case OnlineOfflineUserStatusValueApi.OFFLINE:
      return false;
    default:
      return assertNever(status);
  }
}

export function transformBidAnnotation(
  annotation: BidAnnotationApi,
): BidAnnotation {
  return {
    id: annotation.id,
    bidId: annotation.bid_id,
    projectId: annotation.project_id,
    segments: annotation.segments.map(segment => {
      return {
        text: segment.text,
        context: {
          id: segment.context.id,
          type: segment.context.type,
        },
      };
    }),
    text: annotation.text,
  };
}

/**
 * Maps a `bidProjectFit` Websocket result onto the same `BidAnnotation` shape
 * the fetch path builds via `transformBidAnnotation`, so a streamed annotation
 * is indistinguishable from a fetched one once it lands on the document.
 *
 * The two payloads differ: the Websocket frame carries camelCase fields and a
 * string context type, while the API response carries snake_case fields and an
 * already-numeric `BidAnnotationContextTypeApi`.
 */
export function transformWebsocketBidProjectFitAnnotation(
  result: BidVectorSearchAnnotationResultAjax,
): BidAnnotation {
  return {
    id: result.id,
    bidId: result.bidId,
    projectId: result.projectId,
    segments: result.annotation.segments.map(segment => ({
      text: segment.text,
      context: {
        id: segment.context.id,
        type: transformBidAnnotationContextType(segment.context.type),
      },
    })),
    text: result.annotation.text,
  };
}

function transformBidAnnotationContextType(
  type: BidVectorSearchContextType,
): BidAnnotationContextTypeApi {
  switch (type) {
    case 'bid':
      return BidAnnotationContextTypeApi.BID;
    case 'profileTagline':
      return BidAnnotationContextTypeApi.PROFILE_TAGLINE;
    case 'profileDescription':
      return BidAnnotationContextTypeApi.PROFILE_DESCRIPTION;
    case 'showcase':
      return BidAnnotationContextTypeApi.SHOWCASE;
    case 'review':
      return BidAnnotationContextTypeApi.REVIEW;
    case 'experience':
      return BidAnnotationContextTypeApi.EXPERIENCE;
    case 'education':
      return BidAnnotationContextTypeApi.EDUCATION;
    default:
      assertNever(type, 'Unknown BidVectorSearchContextType');
  }
}

/** See T326629 for the reason */
const BID_RELEVANCY_SCORE_THRESHOLD = 0.7;
const BID_RELEVANCY_SCORE_FACTOR = 2.0;

/**
 * Separates bids into different sections, each (isolated) section will be sorted differently
 * Section with higher value will be placed in front of sections with lower value
 */
enum BidRankSection {
  /** Bids with relevancy score greater than or equal the given threshold (default: BID_RELEVANCY_SCORE_THRESHOLD) */
  RELEVANCY_SCORE = 1,
  /** Bids with relevancy score below the threshold */
  REPUTATION = 0,
}

/**
 * The subset of a bid that bid rank ordering reads.
 */
export type BidRankFields = Pick<
  ProjectViewBid,
  | 'relevanceLabel'
  | 'awardStatus'
  | 'ratingValue'
  | 'score'
  | 'recommended'
  | 'sponsored'
  | 'adjustedScore'
  | 'annotation'
  | 'isVideoBid'
  | 'language'
>;

/**
 * Bid rank inputs that do not live on the bid itself. An absent option holds
 * its tier constant across the whole list, leaving the order unchanged.
 */
export interface BidRankOptions {
  /**
   * T336775: the control gets none of the tiers this test adds, so it keeps the
   * order it had before the test.
   */
  readonly isT336775LanguageMatchedBidsTestVariant: boolean;
  /** Freelancer language code of the project the bids belong to. */
  readonly projectLanguage?: string;
  /**
   * Freelancer language code the project's owner reads the site in. Need not
   * match `projectLanguage`.
   */
  readonly projectOwnerLanguage?: string;
  /**
   * Freelancer language codes someone from the project owner's country could
   * speak natively, from the `countryLanguages` collection. Undefined until
   * that fetch resolves, and for an owner with no country on their profile.
   */
  readonly projectOwnerCountryLanguages?: readonly string[];
}

interface CompareSpec<Name extends string> {
  readonly name: Name;
  readonly order: 'asc' | 'desc';
  /**
   * function should return a number whose natural ordering is the ascending
   * order of that segment. Infinities are fine, the comparator never subtracts.
   *
   * Declared as a method taking `unknown` so every concrete `toValue` satisfies
   * it without the constraint itself introducing an `any`. Each spec's own
   * parameter type is what `BidRankTiers` is derived from, so keep them narrow.
   */
  toValue(value: unknown): number;
}

/**
 * Creates a comparator based on the provided specifications.
 * Each specification names a segment, says how to turn it into a number, and
 * whether that segment sorts ascending or descending. Segments are compared in
 * order and the first difference wins, so the specs are listed most significant
 * first.
 * The comparator takes objects keyed by segment name, so segments can be
 * reordered, added, or removed here without touching any call site.
 */
function comparatorFactory<
  Names extends string,
  Specs extends readonly CompareSpec<Names>[],
>(
  specs: Specs,
): (
  a: {
    [Spec in Specs[number] as Spec['name']]: Parameters<Spec['toValue']>[0];
  },
  b: {
    [Spec in Specs[number] as Spec['name']]: Parameters<Spec['toValue']>[0];
  },
) => number {
  return (a, b) => {
    for (const { name, toValue, order } of specs) {
      const difference = compareAscending(toValue(a[name]), toValue(b[name]));

      const value = order === 'desc' ? -difference : difference;

      if (value !== 0) {
        return value;
      }
    }

    return 0;
  };
}

/** Ascending, and safe for Infinity where subtraction would produce NaN */
function compareAscending(a: number, b: number): number {
  if (a === b) {
    return 0;
  }

  /**
   * NaN compares false against everything, so without these two branches both
   * `compareAscending(NaN, x)` and `compareAscending(x, NaN)` would answer "less
   * than". That is not a valid comparator and lets `Array.sort` return an
   * engine-dependent permutation of the whole list. NaN ranks above every real
   * number here, so under the descending tiers it lands first - where the bid
   * rank sort key put it too.
   */
  if (Number.isNaN(a)) {
    return Number.isNaN(b) ? 0 : 1;
  }

  if (Number.isNaN(b)) {
    return -1;
  }

  return a > b ? 1 : -1;
}

function booleanToRankValue(value: boolean): number {
  return value ? 1 : 0;
}

/**
 * The two identity functions below are load-bearing, not dead indirection:
 * `BidRankTiers` is derived from `Parameters<Spec['toValue']>[0]`, so a spec's
 * `toValue` is the only thing that gives its tier a type. `sectionToRankValue`
 * is what makes the `section` tier a `BidRankSection` instead of a bare number.
 */
function numberToRankValue(value: number): number {
  return value;
}

function sectionToRankValue(section: BidRankSection): number {
  return section;
}

/**
 * The ranking tiers, most significant first, all sorted descending.
 *
 * Quirks of the ordering, kept deliberately:
 *  - an unrated bid ranks as NEGATIVE_INFINITY, below every rated bid
 *  - the recommended and sponsored tiers are inert for rated bids
 *  - a NEGATIVE_INFINITY score counts as 0.0
 *  - the language, video and annotation tiers all outrank the relevancy
 *    section, so a bid with any of them, below the relevancy threshold, sits
 *    ahead of a bid with none above it
 *  - owner language outranks project language outranks video outranks
 *    annotation, so the four booleans order lexicographically
 *
 * To change the ordering, reorder, add, or drop a spec here. The tier object
 * type follows, so `createBidRankTiers` fails to compile until it supplies the
 * new segment.
 */
const compareBidRankTiers = comparatorFactory([
  {
    // Place bids with more relevance in front of the list
    name: 'isMoreRelevant',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    name: 'hasAwardStatus',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    name: 'rating',
    toValue: numberToRankValue,
    order: 'desc',
  },
  {
    name: 'recommended',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    name: 'sponsored',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    /**
     * T337491: surface the bids in a language the project's owner reads
     * natively - their site language (T337856) or a country one (T337857).
     */
    name: 'isNativeBid',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    /** T337222: surface the bids in the project's language */
    name: 'matchesProjectLanguage',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    /** T337222: surface the bids with a video pitch */
    name: 'isVideoBid',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    /** T337222: surface the bids with annotation */
    name: 'hasAnnotation',
    toValue: booleanToRankValue,
    order: 'desc',
  },
  {
    name: 'section',
    toValue: sectionToRankValue,
    order: 'desc',
  },
  {
    name: 'score',
    toValue: numberToRankValue,
    order: 'desc',
  },
] as const);

/** Derived from the specs, so the two can never drift apart */
type BidRankTiers = Parameters<typeof compareBidRankTiers>[0];

/**
 * The tiers T336775 adds. The control gets `false` for every one of them, and a
 * tier held constant across the whole list cannot reorder it, so the control
 * keeps the order it had before the test.
 */
function languageMatchedTiers(
  bid: BidRankFields,
  options?: BidRankOptions,
): Pick<
  BidRankTiers,
  'isNativeBid' | 'matchesProjectLanguage' | 'isVideoBid' | 'hasAnnotation'
> {
  if (options?.isT336775LanguageMatchedBidsTestVariant !== true) {
    return {
      isNativeBid: false,
      matchesProjectLanguage: false,
      isVideoBid: false,
      hasAnnotation: false,
    };
  }

  return {
    isNativeBid:
      bid.language !== undefined &&
      (bid.language === options.projectOwnerLanguage ||
        (options.projectOwnerCountryLanguages?.includes(bid.language) ??
          false)),
    // Both guards are load-bearing: two unknown languages are not a match.
    // Codes are compared as is, both being legacy Freelancer codes, not ISO.
    matchesProjectLanguage:
      options.projectLanguage !== undefined &&
      bid.language !== undefined &&
      bid.language === options.projectLanguage,
    isVideoBid: bid.isVideoBid,
    hasAnnotation: bid.annotation !== undefined,
  };
}

function createBidRankTiers(
  bid: BidRankFields,
  options?: BidRankOptions,
): BidRankTiers {
  const isMoreRelevant = bid.relevanceLabel !== DisplayBidRelevancyLabelApi.C;
  const hasAwardStatus = bid.awardStatus !== undefined;

  /**
   * The document stores NEGATIVE_INFINITY when the API omits a score, but bid
   * ranking has always treated a missing score as 0.0. See the `score ?? 0.0`
   * in `transformProjectViewBids` and the same mapping in the reducer.
   */
  const score = bid.score === Number.NEGATIVE_INFINITY ? 0.0 : bid.score;

  const relevancyScore =
    bid.adjustedScore !== undefined
      ? calculateBidRelevancyScore({
          adjustedScore: bid.adjustedScore,
          originalScore: score,
          relevancyScoreFactor: BID_RELEVANCY_SCORE_FACTOR,
        })
      : undefined;

  const section =
    relevancyScore !== undefined &&
    relevancyScore >= BID_RELEVANCY_SCORE_THRESHOLD
      ? BidRankSection.RELEVANCY_SCORE
      : BidRankSection.REPUTATION;

  const normalizedRating = normalizeBidRating(bid.ratingValue);
  const isSponsored = bid.sponsored !== undefined && bid.sponsored !== 0;

  if (normalizedRating === undefined) {
    return {
      isMoreRelevant,
      hasAwardStatus,
      // Below any real rating, so every rated bid outranks this one
      rating: Number.NEGATIVE_INFINITY,
      recommended: bid.recommended,
      sponsored: isSponsored,
      ...languageMatchedTiers(bid, options),
      section,
      score,
    };
  }

  return {
    isMoreRelevant,
    hasAwardStatus,
    rating: normalizedRating,
    ...languageMatchedTiers(bid, options),
    section,
    score,

    // A rating outranks both of these, so they are held constant to disregard them
    recommended: false,
    sponsored: false,
  };
}

/**
 * Orders two bids the way the bid list's Recommended option orders them.
 *
 * `options` is the list's context, not a property of either bid, so pass the
 * same ones for both.
 */
export function compareBidRank(
  a: BidRankFields,
  b: BidRankFields,
  options?: BidRankOptions,
): number {
  return compareBidRankTiers(
    createBidRankTiers(a, options),
    createBidRankTiers(b, options),
  );
}

/**
 * Returns a new array ordered by bid rank. The input is left untouched, and
 * bids that rank equally keep the order they arrived in.
 */
export function sortBidsByRank<T extends BidRankFields>(
  bids: readonly T[],
  options: BidRankOptions,
): readonly T[] {
  return bids
    .map(bid => ({ bid, tiers: createBidRankTiers(bid, options) }))
    .sort((a, b) => compareBidRankTiers(a.tiers, b.tiers))
    .map(({ bid }) => bid);
}

/**
 * Inverse of BidVectorSearchRelevancyScoreAdditionStrategy
 */
function calculateBidRelevancyScore(options: {
  readonly adjustedScore: number;
  readonly originalScore: number;
  readonly relevancyScoreFactor: number;
}): number {
  const { adjustedScore, originalScore, relevancyScoreFactor } = options;

  return (adjustedScore - originalScore) / relevancyScoreFactor;
}

/**
 * Map bid rating:
 *  - None and 0        -> None (treat them the same)
 *  - 1, 2, 3, 4, 5     -> Keep as is
 */
function normalizeBidRating(rating: number | undefined): number | undefined {
  if (rating === undefined || rating === 0.0) {
    return undefined;
  }

  return rating;
}
